import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../services/api';
import { ClientRequest } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import Layout from '../components/Layout';
import { ArrowLeft, Save, UserCheck, AlertCircle, HelpCircle } from 'lucide-react';

const ClientCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();
  const { startTour, manualStart } = useOnboardingTour();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [formData, setFormData] = useState<ClientRequest>({
    companyId: selectedCompany.selectedCompanyId || '',
    clientNumber: 0,
    name: '',
    email: '',
    phone: '',
    ice: '',
    street: '',
    city: '',
    postalCode: '',
    country: '',
    website: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCompany.selectedCompanyId) {
      setError(t('clients.pleaseSelectCompanyFirst'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newClient = await clientAPI.createClient({
        ...formData,
        companyId: selectedCompany.selectedCompanyId
      });

      // Navigate to client list with success message
      navigate('/clients', {
        state: {
          message: t('clients.clientCreatedSuccessfully', { name: newClient.name }),
          type: 'success'
        }
      });
    } catch (err) {
      setError(t('clients.failedToCreateClient'));
      console.error('Error creating client:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ClientRequest, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Increment refresh key on component mount to force client number fetch
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Fetch next client number when component mounts or company changes
  useEffect(() => {
    const fetchNextClientNumber = async () => {
      if (selectedCompany.selectedCompanyId) {
        try {
          const nextNumber = await clientAPI.getNextClientNumber(selectedCompany.selectedCompanyId);
          setFormData(prev => ({
            ...prev,
            clientNumber: nextNumber
          }));
        } catch (error) {
          console.error('Error fetching next client number:', error);
          // Keep the default value of 0 if fetching fails
        }
      }
    };

    fetchNextClientNumber();
  }, [selectedCompany.selectedCompanyId, refreshKey]); // Run when company changes or component mounts

  // Auto-start onboarding tour on first visit
  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      startTour({
        tourId: 'client-create',
        steps: getClientTourSteps(),
        autoStartDelay: 1000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany.selectedCompanyId]);

  if (!selectedCompany.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Company Selected</h2>
            <p className="text-gray-600 mb-4">Please select a company from the top navigation to create clients.</p>
            <button
              onClick={() => navigate('/companies')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Go to Companies
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
            <button
              onClick={() => navigate('/clients')}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} text-gray-600 hover:text-gray-900 transition-colors`}
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('clients.backToClients')}</span>
            </button>
          </div>
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
            <UserCheck className="w-8 h-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">{t('clients.createNewClient')}</h1>
          </div>
        </div>

        {/* Company Info */}
        {selectedCompany.selectedCompany && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-blue-900">{t('clients.creatingClientFor')}</h3>
                <p className="text-sm text-blue-700">{selectedCompany.selectedCompany.name}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="p-8">
            {/* Basic Information */}
            <div className="mb-8" id="client-basic-info">
              <h2 className={`text-xl font-semibold text-gray-900 mb-4 flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                <UserCheck className="w-5 h-5" />
                <span>{t('clients.basicInformation')}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.clientNumber')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.clientNumber || ''}
                    onChange={(e) => handleInputChange('clientNumber', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.clientNumberPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.clientName')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.clientNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.email')} *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.phone')} *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.phonePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.ice')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.ice || ''}
                    onChange={(e) => handleInputChange('ice', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.icePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.website')}
                  </label>
                  <input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.websitePlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="mb-8" id="client-address-info">
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('clients.addressInformation')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.street')}
                  </label>
                  <input
                    type="text"
                    value={formData.street || ''}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.streetPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.city')}
                  </label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.cityPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.postalCode')}
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode || ''}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.postalCodePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.country')}
                  </label>
                  <input
                    type="text"
                    value={formData.country || ''}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.countryPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-8" id="client-additional-info">
              <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('clients.additionalInformation')}</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('clients.notes')}
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('clients.notesPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div id="client-submit-actions" className={`flex items-center justify-end ${isRTL ? 'space-x-reverse' : 'space-x-4'} pt-6 border-t border-gray-200`}>
              <button
                type="button"
                onClick={() => navigate('/clients')}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('clients.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('clients.creating')}</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>{t('clients.createClient')}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Onboarding Tour Help Button */}
      <button
        type="button"
        className="onboarding-help-btn"
        title={t('onboarding.replayTour')}
        onClick={() => {
          manualStart({
            tourId: 'client-create',
            steps: getClientTourSteps(),
          });
        }}
      >
        <HelpCircle size={22} />
      </button>
    </Layout>
  );

  function getClientTourSteps() {
    return [
      {
        element: '#client-basic-info',
        popover: {
          title: t('onboarding.client.basicInfoTitle'),
          description: t('onboarding.client.basicInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#client-address-info',
        popover: {
          title: t('onboarding.client.addressInfoTitle'),
          description: t('onboarding.client.addressInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#client-additional-info',
        popover: {
          title: t('onboarding.client.additionalInfoTitle'),
          description: t('onboarding.client.additionalInfoDesc'),
          side: 'bottom' as const,
          align: 'start' as const,
        },
      },
      {
        element: '#client-submit-actions',
        popover: {
          title: t('onboarding.client.submitTitle'),
          description: t('onboarding.client.submitDesc'),
          side: 'top' as const,
          align: 'end' as const,
        },
      },
    ];
  }
};

export default ClientCreatePage;
